'use client';

/**
 * Global Error Boundary
 *
 * Next.js App Routerの最上位エラーバウンダリ
 * Reactコンポーネント内で発生したキャッチされていないエラーをキャプチャ
 *
 * @see https://nextjs.org/docs/app/building-your-application/routing/error-handling
 */

import * as Sentry from '@sentry/nextjs';
import { useEffect, useState } from 'react';

/**
 * Service Workerのキャッシュをクリアしてページをリロード
 * デプロイ後の古いキャッシュによるSyntaxError等の問題を自動復旧
 */
async function clearCachesAndReload(): Promise<void> {
  try {
    // Service Workerを解除
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }

    // Cache Storageをクリア
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    // ページをリロード（キャッシュをバイパス）
    window.location.reload();
  } catch (e) {
    console.error('Failed to clear caches:', e);
    window.location.reload();
  }
}

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const [isClearing, setIsClearing] = useState(false);

  useEffect(() => {
    // Sentryにエラーを送信
    Sentry.captureException(error);

    // SyntaxErrorの場合は古いキャッシュが原因の可能性が高いため、自動でキャッシュクリアを試行
    if (error.name === 'SyntaxError' || error.message?.includes('SyntaxError')) {
      clearCachesAndReload();
    }
  }, [error]);

  return (
    <html lang="ja">
      <body>
        <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-red-100">
                <svg className="size-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">エラーが発生しました</h2>
              <p className="text-sm text-gray-600">
                予期しないエラーが発生しました。
                <br />
                しばらくしてから再度お試しください。
              </p>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <div className="mb-6 rounded-md bg-gray-100 p-4">
                <p className="mb-2 text-xs font-semibold text-gray-700">開発モード - エラー詳細:</p>
                <p className="text-xs text-red-600">{error.message}</p>
                {error.digest && <p className="mt-1 text-xs text-gray-500">Digest: {error.digest}</p>}
              </div>
            )}

            <button
              onClick={() => reset()}
              className="w-full rounded-md bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              再試行
            </button>

            <button
              onClick={() => (window.location.href = '/')}
              className="mt-3 w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              ホームに戻る
            </button>

            <button
              onClick={() => {
                setIsClearing(true);
                clearCachesAndReload();
              }}
              disabled={isClearing}
              className="mt-3 w-full rounded-md border border-orange-300 bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-700 transition-colors hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isClearing ? 'キャッシュをクリア中...' : 'キャッシュをクリアして再読み込み'}
            </button>
          </div>

          <p className="mt-8 text-xs text-gray-500">問題が解決しない場合は、サポートチームにお問い合わせください。</p>
        </div>
      </body>
    </html>
  );
}
